import type { AccessRead, ScopedAccessRead } from '@/client'

/**
 * Reading `GET /v1/access` — what this principal may do, and where.
 *
 * `permissions` lists every permission held anywhere; `tenant_wide` the ones
 * held everywhere; `scoped` the rest, each with the ids it is limited to.
 * These helpers answer the questions a page asks: may I show this, may I
 * show it as a page about the whole organisation, may I let them edit it,
 * and which stores do I filter to.
 *
 * Hiding a control is UX, not authorization (rules.md H7). The backend still
 * decides; this only keeps the UI from offering what will be refused.
 */

/** `ads`, `tickets`, `catalogue`… — the part before the colon. */
export type AccessModule = string
export type Permission = string

/** The id lists a scoped grant carries. `all_of_kinds` is a kind-wide flag, not an id list. */
export type ScopeKey = keyof Omit<ScopedAccessRead, 'permission' | 'all_of_kinds'>

/** Held anywhere — tenant-wide or in at least one scope. */
export function can(access: AccessRead | undefined, permission: Permission): boolean {
  return access?.permissions.includes(permission) ?? false
}

/** `<module>:edit` is held anywhere. */
export function canEdit(access: AccessRead | undefined, module: AccessModule): boolean {
  return can(access, `${module}:edit`)
}

/**
 * Held across the whole organisation. What a page about the organisation
 * itself needs — its engine settings, running every rule, the dashboard's
 * totals — where access to one store is no access at all.
 */
export function canEverywhere(access: AccessRead | undefined, permission: Permission): boolean {
  return access?.tenant_wide.includes(permission) ?? false
}

/** Held, but only within some scopes — the page should filter to them. */
export function isRestricted(access: AccessRead | undefined, permission: Permission): boolean {
  return can(access, permission) && !canEverywhere(access, permission)
}

/**
 * The ids a permission is limited to: `undefined` when it is held
 * tenant-wide (or not at all — nothing to filter to), otherwise the union of
 * every scoped grant's ids, deduplicated and in first-seen order.
 */
export function grantedIds(
  access: AccessRead | undefined,
  permission: Permission,
  key: ScopeKey,
): number[] | undefined {
  if (!isRestricted(access, permission)) return undefined
  const ids = new Set<number>()
  for (const scope of access?.scoped ?? []) {
    if (scope.permission !== permission) continue
    for (const id of scope[key]) ids.add(id)
  }
  return [...ids]
}
